<template>
  <div :class="['rx-tree', 'rx-tree--' + variant]" role="tree">
    <template v-if="normalizedQuery">
      <div v-if="matches.length === 0" class="rx-tree__empty">No matching folders</div>
      <div
        v-for="m in matches"
        :key="'m-' + m.node.id"
        :class="['rx-tree__row', 'rx-tree__row--match', m.node.id === currentId ? 'is-current' : '', disabled(m.node) ? 'is-disabled' : '']"
        role="treeitem"
        :aria-disabled="disabled(m.node) ? 'true' : null"
        tabindex="0"
        @click="navigate(m.node)"
        @keydown.enter="navigate(m.node)">
        <span class="rx-tree__icon"><i class="far fa-folder"></i></span>
        <span class="rx-tree__text">
          <span class="rx-tree__name">{{ m.node.name }}</span>
          <span class="rx-tree__path">{{ m.path }}</span>
        </span>
      </div>
    </template>
    <template v-else>
      <div v-if="rows.length === 0 && rootLoading" class="rx-tree__empty">Loading…</div>
      <template v-for="row in rows">
        <div
          v-if="row.type === 'node'"
          :key="'n-' + row.node.id"
          :class="['rx-tree__row', row.node.id === currentId ? 'is-current' : '', disabled(row.node) ? 'is-disabled' : '', row.node.isArchived ? 'is-archived' : '', dropTargetId === row.node.id ? 'is-drop-target' : '']"
          :aria-disabled="disabled(row.node) ? 'true' : null"
          :style="{ paddingLeft: (row.depth * indent + 4) + 'px' }"
          role="treeitem"
          :aria-expanded="hasChildren(row.node) && !row.fixed ? String(!!expanded[row.node.id]) : null"
          @click="navigate(row.node)"
          @dragover="onDragOver(row.node, $event)"
          @dragleave="onDragLeave(row.node, $event)"
          @drop="onDrop(row.node, $event)">
          <button
            v-if="hasChildren(row.node) && !row.fixed"
            type="button"
            class="rx-tree__chevron"
            :aria-label="expanded[row.node.id] ? 'Collapse ' + row.node.name : 'Expand ' + row.node.name"
            @click.stop="toggle(row.node)">
            <i :class="['fas', expanded[row.node.id] ? 'fa-chevron-down' : 'fa-chevron-right']"></i>
          </button>
          <span v-else class="rx-tree__chevron rx-tree__chevron--spacer"></span>
          <span class="rx-tree__icon"><i class="far fa-folder"></i></span>
          <span class="rx-tree__name" :title="row.node.name">{{ row.node.name }}</span>
          <span v-if="row.node.isArchived" class="rx-pill">Archived</span>
          <span v-if="row.node.childCount > 0" class="rx-badge">{{ row.node.childCount.toLocaleString() }}</span>
        </div>
        <div
          v-else-if="row.type === 'loading'"
          :key="'l-' + row.parentId"
          class="rx-tree__row rx-tree__row--muted"
          :style="{ paddingLeft: (row.depth * indent + 4 + chevronWidth) + 'px' }">
          Loading…
        </div>
        <div
          v-else-if="row.type === 'more'"
          :key="'more-' + row.parentId"
          class="rx-tree__row rx-tree__row--more"
          :style="{ paddingLeft: (row.depth * indent + 4 + chevronWidth) + 'px' }">
          <button type="button" class="rx-link" @click.stop="$emit('show-all', row.parentId)">+ {{ row.count }} more properties</button>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { ROOT_ID, compareByName, pathOf } from './resourcesData'

export default {
  name: 'FolderTree',
  props: {
    nodes: { type: Object, required: true },
    currentId: { type: String, default: ROOT_ID },
    expanded: { type: Object, required: true },
    variant: { type: String, default: 'desktop' },
    // 0 = no limit. Otherwise only the first N children of the Properties folder are shown.
    collapseLimit: { type: Number, default: 0 },
    showAll: { type: Object, default: function () { return {} } },
    query: { type: String, default: '' },
    // "Choose destination" mode: function (node) => true when the row cannot be chosen (grayed, not clickable).
    isDisabled: { type: Function, default: null },
    showRoot: { type: Boolean, default: false },
    // Desktop drag-and-drop upload (F4a): function (node) => true when files may be dropped on the row.
    // When set, rows emit `drop-files` (node, DragEvent) for every file drop (valid or not).
    canDrop: { type: Function, default: null }
  },
  data () {
    return {
      dropTargetId: null
    }
  },
  computed: {
    indent () {
      return this.variant === 'phone' ? 32 : 18
    },
    chevronWidth () {
      return this.variant === 'phone' ? 44 : 24
    },
    rootLoading () {
      var root = this.nodes[ROOT_ID]
      return !root || !root.loaded
    },
    normalizedQuery () {
      return (this.query || '').trim().toLowerCase()
    },
    rows () {
      var self = this
      var out = []
      function walk (parentId, depth) {
        var parent = self.nodes[parentId]
        if (!parent) return
        var kids = self.folderChildren(parent)
        var limited = kids
        var hidden = 0
        if (self.collapseLimit > 0 && parent.isPropertyGroup && !self.showAll[parent.id] && kids.length > self.collapseLimit) {
          limited = kids.slice(0, self.collapseLimit)
          hidden = kids.length - self.collapseLimit
        }
        limited.forEach(function (child) {
          out.push({ type: 'node', node: child, depth: depth })
          if (self.expanded[child.id]) {
            if (!child.loaded) {
              out.push({ type: 'loading', parentId: child.id, depth: depth + 1 })
            } else {
              walk(child.id, depth + 1)
            }
          }
        })
        if (hidden > 0) {
          out.push({ type: 'more', parentId: parent.id, depth: depth, count: hidden })
        }
      }
      var root = self.nodes[ROOT_ID]
      if (self.showRoot && root && root.loaded) {
        // Root row is always expanded (destination pickers need "Resources" itself as a target)
        out.push({ type: 'node', node: root, depth: 0, fixed: true })
        walk(ROOT_ID, 1)
      } else {
        walk(ROOT_ID, 0)
      }
      return out
    },
    matches () {
      var self = this
      var q = this.normalizedQuery
      var out = []
      Object.keys(this.nodes).forEach(function (id) {
        var n = self.nodes[id]
        if (n.kind !== 'folder' || id === ROOT_ID) return
        if (n.name.toLowerCase().indexOf(q) === -1) return
        // Skip folders no longer listed by their parent (archived while hidden, deleted, moved away)
        var parent = n.parentId ? self.nodes[n.parentId] : null
        if (parent && parent.loaded && parent.childIds.indexOf(id) === -1) return
        var chain = pathOf(self.nodes, id).map(function (p) { return p.name })
        chain.pop()
        out.push({ node: n, path: chain.join(' › ') })
      })
      out.sort(function (a, b) { return compareByName(a.node, b.node) })
      return out.slice(0, 200)
    }
  },
  methods: {
    folderChildren (parent) {
      var self = this
      return parent.childIds
        .map(function (id) { return self.nodes[id] })
        .filter(function (n) { return n && n.kind === 'folder' })
        .sort(compareByName)
    },
    hasChildren (node) {
      if (!node.loaded) return node.childCount === null || node.childCount > 0
      return node.folderCount > 0
    },
    toggle (node) {
      this.$emit('toggle', node)
    },
    disabled (node) {
      return this.isDisabled ? this.isDisabled(node) === true : false
    },
    navigate (node) {
      if (this.disabled(node)) return
      this.$emit('navigate', node)
    },
    isFileDrag (e) {
      var types = e && e.dataTransfer ? e.dataTransfer.types : null
      if (!types) return false
      for (var i = 0; i < types.length; i++) {
        if (types[i] === 'Files') return true
      }
      return false
    },
    onDragOver (node, e) {
      if (!this.canDrop || !this.isFileDrag(e)) return
      e.preventDefault()
      e.stopPropagation()
      var ok = this.canDrop(node) === true
      e.dataTransfer.dropEffect = 'copy'
      this.dropTargetId = ok ? node.id : null
    },
    onDragLeave (node, e) {
      if (!this.canDrop) return
      var to = e.relatedTarget
      if (to && e.currentTarget && e.currentTarget.contains(to)) return
      if (this.dropTargetId === node.id) this.dropTargetId = null
    },
    onDrop (node, e) {
      if (!this.canDrop || !this.isFileDrag(e)) return
      e.preventDefault()
      e.stopPropagation()
      this.dropTargetId = null
      this.$emit('drop-files', node, e)
    },
    clearDropTarget () {
      this.dropTargetId = null
    }
  }
}
</script>

<style scoped>
.rx-tree__row {
  display: flex;
  align-items: center;
  min-height: 40px;
  padding-right: 8px;
  cursor: pointer;
  color: #1f2937;
  border-radius: 6px;
}
.rx-tree--phone .rx-tree__row {
  min-height: 56px;
  border-radius: 0;
  border-bottom: 1px solid #eef0f2;
  font-size: 16px;
}
.rx-tree__row:hover {
  background: #f7f8fa;
}
.rx-tree__row.is-current {
  background: #e3f1ef;
  font-weight: 700;
}
.rx-tree__row.is-disabled {
  color: #9ca3af;
  cursor: default;
}
.rx-tree__row.is-disabled .rx-tree__icon {
  color: #c4c9d0;
}
.rx-tree__row.is-disabled:hover {
  background: transparent;
}
.rx-tree__row.is-drop-target {
  outline: 2px dashed #1a7f86;
  outline-offset: -2px;
  background: #e3f1ef;
}
.rx-tree__row.is-archived .rx-tree__icon,
.rx-tree__row.is-archived .rx-tree__name {
  opacity: 0.55;
}
.rx-pill {
  flex: 0 0 auto;
  margin-left: 6px;
  padding: 2px 6px;
  border-radius: 999px;
  background: #e5e7eb;
  color: #4b5563;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
}
.rx-tree__row--muted {
  color: #6b7280;
  cursor: default;
  font-size: 14px;
}
.rx-tree__row--more {
  cursor: default;
}
.rx-tree__row--more:hover,
.rx-tree__row--muted:hover {
  background: transparent;
}
.rx-tree__chevron {
  flex: 0 0 24px;
  width: 24px;
  height: 32px;
  border: 0;
  background: transparent;
  color: #6b7280;
  padding: 0;
  font-size: 11px;
  cursor: pointer;
}
.rx-tree--phone .rx-tree__chevron {
  flex-basis: 44px;
  width: 44px;
  height: 44px;
  font-size: 13px;
}
.rx-tree__chevron--spacer {
  cursor: default;
}
.rx-tree__icon {
  flex: 0 0 auto;
  color: #2f6fa3;
  margin-right: 8px;
}
.rx-tree__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1 1 auto;
}
.rx-tree__name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.rx-tree__path {
  font-size: 14px;
  color: #6b7280;
  font-weight: 400;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.rx-tree__row--match {
  padding-left: 12px;
  min-height: 56px;
}
.rx-tree__empty {
  padding: 16px;
  color: #6b7280;
  font-size: 14px;
}
.rx-badge {
  flex: 0 0 auto;
  margin-left: 8px;
  background: #0f6e70;
  color: #fff;
  font-weight: 700;
  font-size: 12px;
  line-height: 1;
  padding: 4px 8px;
  border-radius: 999px;
}
.rx-link {
  border: 0;
  background: transparent;
  padding: 0;
  color: #1a7f86;
  font-size: 14px;
  cursor: pointer;
}
.rx-link:hover {
  text-decoration: underline;
}
</style>
